import clsx from 'clsx';
import type { CompareCompact3AxisResult } from '@arcanum-hq/shared-types';
import type { ToolCallState } from '@/hooks/use-streaming-v2';
import { PhaseShell } from './ToolResultRendererSkeleton';
import { Skeleton } from '@/components/ui/Skeleton';
import { formatNumber } from '@/lib/format';

interface ScenarioSummaryCardProps {
  state: ToolCallState;
}

function isCompact3AxisResult(data: unknown): data is CompareCompact3AxisResult {
  return (
    typeof data === 'object' &&
    data !== null &&
    'kind' in data &&
    (data as { kind: unknown }).kind === 'compact_3axis'
  );
}

const SIGNAL_LABEL: Record<string, string> = {
  flat: '─ no change',
  symmetric: '⇄ balanced',
  'downside-skewed': '↓ downside heavy',
  'upside-skewed': '↑ upside heavy',
};

const SIGNAL_TOKEN: Record<string, string> = {
  flat: 'text-text-dim',
  symmetric: 'text-text-muted',
  'downside-skewed': 'text-danger',
  'upside-skewed': 'text-accent',
};

function getOutputKeys(result: CompareCompact3AxisResult): string[] {
  const keys = new Set<string>();
  for (const k of Object.keys(result.base.outputs)) keys.add(k);
  for (const k of Object.keys(result.downside.outputs)) keys.add(k);
  for (const k of Object.keys(result.upside.outputs)) keys.add(k);
  return Array.from(keys);
}

function formatCell(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return formatNumber(value, 4);
  }
  return value == null ? '—' : String(value);
}

function deltaPp(value: unknown, base: unknown): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  if (typeof base !== 'number' || !Number.isFinite(base)) return null;
  const delta = value - base;
  if (delta === 0) return null;
  const sign = delta > 0 ? '+' : '';
  return `${sign}${formatNumber(delta, 4)}`;
}

function renderComplete(result: CompareCompact3AxisResult) {
  const outputs = getOutputKeys(result);
  const signals = result.summary?.signalByOutput ?? {};

  return (
    <div className="space-y-2" data-testid="scenario-summary-card">
      <div className="text-xs font-mono font-semibold text-accent tracking-[1.2px] uppercase">
        Downside / Base / Upside
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-panel-border">
              <th className="text-left text-xs font-mono font-semibold text-text-muted uppercase tracking-[1px] px-2 py-1">
                Metric
              </th>
              <th className="text-right text-xs font-mono font-semibold text-danger uppercase tracking-[1px] px-2 py-1">
                Downside
              </th>
              <th className="text-right text-xs font-mono font-semibold text-text-bright uppercase tracking-[1px] px-2 py-1">
                Base
              </th>
              <th className="text-right text-xs font-mono font-semibold text-accent uppercase tracking-[1px] px-2 py-1">
                Upside
              </th>
              <th className="text-right text-xs font-mono font-semibold text-text-muted uppercase tracking-[1px] px-2 py-1">
                Signal
              </th>
            </tr>
          </thead>
          <tbody>
            {outputs.map((key) => {
              const baseValue = result.base.outputs[key];
              const downValue = result.downside.outputs[key];
              const upValue = result.upside.outputs[key];
              const downDelta = deltaPp(downValue, baseValue);
              const upDelta = deltaPp(upValue, baseValue);
              const signal = signals[key];
              return (
                <tr
                  key={key}
                  data-testid="scenario-summary-row"
                  data-metric={key}
                  className="border-b border-panel-border/50 hover:bg-accent-faint transition-colors"
                >
                  <td className="text-sm font-mono text-text-muted px-2 py-1">{key}</td>
                  <td className="text-right px-2 py-1">
                    <div className="text-sm font-mono tabular-nums text-text">
                      {formatCell(downValue)}
                    </div>
                    {downDelta && (
                      <div className="text-xs font-mono tabular-nums text-danger">
                        {downDelta}
                      </div>
                    )}
                  </td>
                  <td className="text-right px-2 py-1 text-sm font-mono tabular-nums text-text-bright">
                    {formatCell(baseValue)}
                  </td>
                  <td className="text-right px-2 py-1">
                    <div className="text-sm font-mono tabular-nums text-text">
                      {formatCell(upValue)}
                    </div>
                    {upDelta && (
                      <div className="text-xs font-mono tabular-nums text-accent">
                        {upDelta}
                      </div>
                    )}
                  </td>
                  <td
                    className={clsx(
                      'text-right px-2 py-1 text-xs font-mono',
                      signal ? SIGNAL_TOKEN[signal] : 'text-text-dim',
                    )}
                    data-signal={signal ?? 'unknown'}
                  >
                    {signal ? SIGNAL_LABEL[signal] : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Phase 4 Session 3 — compact_3axis progressive paint.
 * Each `scenario_computed` event fills its row (downside / base / upside);
 * un-painted rows render with `(pending)` until `scenarios_complete` flips
 * them to `(aborted)`.
 */
function renderInProgress(state: Extract<ToolCallState, { phase: 'executing' }>) {
  const startedEvent = state.progress.find((p) => p.phase === 'scenarios_started');
  const scenarioEvents = state.progress.filter((p) => p.phase === 'scenario_computed');
  const allTerminated = state.progress.some((p) => p.phase === 'scenarios_complete');
  const outputNames =
    (startedEvent?.payload as { output_names?: string[] } | undefined)?.output_names ?? [];
  const fixedRows: Array<'downside' | 'base' | 'upside'> = ['downside', 'base', 'upside'];
  const ROW_TITLE: Record<typeof fixedRows[number], string> = {
    downside: 'Downside',
    base: 'Base',
    upside: 'Upside',
  };

  return (
    <PhaseShell variant="in-progress" title="Comparing scenarios…">
      <div className="overflow-x-auto" data-testid="scenario-summary-progress">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-panel-border">
              <th className="text-left text-xs font-mono font-semibold text-text-muted uppercase tracking-[1px] px-2 py-1">
                Scenario
              </th>
              {outputNames.length === 0 && (
                <th className="text-right text-xs font-mono font-semibold text-text-muted uppercase tracking-[1px] px-2 py-1">
                  …
                </th>
              )}
              {outputNames.map((name) => (
                <th
                  key={name}
                  className="text-right text-xs font-mono font-semibold text-text-muted uppercase tracking-[1px] px-2 py-1"
                >
                  {name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fixedRows.map((rowName) => {
              const event = scenarioEvents.find(
                (e) => (e.payload as { scenario_name: string }).scenario_name === rowName,
              );
              const aborted = !event && allTerminated;
              const status = event ? 'painted' : aborted ? 'aborted' : 'pending';
              if (!event) {
                return (
                  <tr
                    key={rowName}
                    data-row-painted="false"
                    data-row-status={status}
                    className="border-b border-panel-border/50 text-text-dim"
                  >
                    <td className="text-sm font-mono px-2 py-1">
                      {ROW_TITLE[rowName]}{' '}
                      <span className="text-xs">({status})</span>
                    </td>
                    {(outputNames.length > 0 ? outputNames : ['placeholder']).map((n) => (
                      <td key={n} className="text-right text-sm font-mono px-2 py-1">
                        —
                      </td>
                    ))}
                  </tr>
                );
              }
              const outputs = (event.payload as { outputs: Record<string, number> }).outputs;
              const cols = outputNames.length > 0 ? outputNames : Object.keys(outputs);
              return (
                <tr
                  key={rowName}
                  data-row-painted="true"
                  data-row-status="painted"
                  className="border-b border-panel-border/50"
                >
                  <td className="text-sm font-mono text-text px-2 py-1">{ROW_TITLE[rowName]}</td>
                  {cols.map((n) => (
                    <td
                      key={n}
                      className="text-right text-sm font-mono text-text px-2 py-1 tabular-nums"
                    >
                      {formatNumber(outputs[n] ?? 0, 4)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </PhaseShell>
  );
}

/**
 * Phase-aware compact 3-axis renderer (Phase 3 Session 4). Routes on
 * `state.phase`; renders the existing JSON dump for unrecognized result
 * shapes (defensive against producer-side regression).
 */
export function ScenarioSummaryCard({ state }: ScenarioSummaryCardProps) {
  if (state.phase === 'complete' && isCompact3AxisResult(state.result)) {
    return renderComplete(state.result);
  }
  if (state.phase === 'complete') {
    // Result is `kind: 'matrix'` (or unknown) — defer to the parent
    // dispatcher which routes matrix results to <ScenarioComparison>.
    return null;
  }
  if (state.phase === 'executing') {
    return renderInProgress(state);
  }
  const variant = state.phase === 'aborted' ? 'aborted' : 'skeleton';
  return (
    <PhaseShell variant={variant} title="Downside / Base / Upside">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-panel-border">
              <th className="text-left text-xs font-mono font-semibold text-text-muted uppercase tracking-[1px] px-2 py-1">
                Metric
              </th>
              <th className="text-right text-xs font-mono font-semibold text-danger uppercase tracking-[1px] px-2 py-1">
                Downside
              </th>
              <th className="text-right text-xs font-mono font-semibold text-text-bright uppercase tracking-[1px] px-2 py-1">
                Base
              </th>
              <th className="text-right text-xs font-mono font-semibold text-accent uppercase tracking-[1px] px-2 py-1">
                Upside
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 3 }).map((_, ri) => (
              <tr key={ri} className="border-b border-panel-border/50">
                <td className="px-2 py-1">
                  <Skeleton className="h-3 w-16" />
                </td>
                <td className="text-right px-2 py-1">
                  <Skeleton className="h-3 w-12 ml-auto" />
                </td>
                <td className="text-right px-2 py-1">
                  <Skeleton className="h-3 w-12 ml-auto" />
                </td>
                <td className="text-right px-2 py-1">
                  <Skeleton className="h-3 w-12 ml-auto" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PhaseShell>
  );
}
